import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { useLocale } from '../i18n/locale-context';
import { AuthApiError } from '../lib/auth-client';
import { NetworkError } from '../lib/api';
import {
  listSavedSearches,
  createNiche,
  deleteSavedSearch,
  sendFirstAlert,
  savedSearchToCatalogPath,
  type NicheFirstMatch,
  type SavedSearch,
} from '../lib/saved-search-client';
import {
  fetchCategories,
  searchIkpu,
  fetchIkpuCodes,
  searchNiches,
  type CategoryItem,
  type IkpuItem,
  type NicheItem,
} from '../lib/catalog-client';
import { CategoryChips } from './category-chips';
import { TagIcon, BellIcon } from './icons';
import { TelegramConnectButton } from './telegram-connect-button';
import type { TelegramBotAuthResult } from './telegram-bot-login';
import {
  readGate,
  writeGate,
  mayShowModal,
  recordShown,
  type OnboardingGateState,
} from '../lib/onboarding-gate';
import { pendingSteps, type OnboardingStepId } from '../lib/onboarding-steps';
import { countryConfig } from '../country';
import { submitCompany, type CompanyEntry, type CompanyPayoff } from '../lib/auth-client';
import { CompanyLookup, tinFromLookup, type CompanyPick } from './company-lookup';
import { companyErrorKey } from '../lib/company-error';
import { useSwipeDownToDismiss } from '../lib/use-swipe-down-to-dismiss';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { useKeyboardSpaceBelow } from '../lib/viewport';
import { useCheckoutSheetOpen } from '../lib/checkout-presence';
import { CompanyPayoffLine } from './company-payoff';
import { daysLeft, daysWord, formatBudget } from '../lib/format';
import { localePath, stripLocalePrefix } from '../i18n/locale-path';
import type { MySelectionState } from '../routes/public/catalog';
import { useAiOnboarding } from '../lib/use-ai-onboarding';
import { AiOnboardingStep } from './ai-onboarding-step';

const TG_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;

/**
 * How much of this sheet's top edge is draggable — more than double the 72 px
 * default.
 *
 * The default is tuned for the CHECKOUT sheet, where a wide zone was the bug:
 * a pan over the price header closed a sheet the buyer was about to type a card
 * into. This sheet has the opposite problem — its top is a step counter, a
 * heading and a sample alert, nothing to fill in — and on a phone the only way
 * out was a 28 px ✕ in the corner. 160 px covers the pill, the counter and the
 * heading, which is the part a thumb actually reaches for.
 */
const ONBOARDING_GRAB_ZONE_PX = 160;
// UZ ИНН = 9 digits, KZ БИН/ИИН = 12 — the web only gates on length; the server
// does the real checksum. See `countryConfig.companyIdForm`.
const TIN_DIGITS = countryConfig.companyIdForm.digits;
// The onboarding "head" of the taxonomy — the top categories by live-tender count,
// shown as quick-pick chips. Data-driven (see headCategories), so slugs are always
// valid catalog filters. Anything outside the head is reachable via the search box.
const HEAD_COUNT = 10;

/**
 * ⚠️ Dismissal is NO LONGER permanent.
 *
 * The v1 rule wrote a `seen` flag on ANY dismissal, so a reflexive tap on the
 * overlay in the first two seconds killed onboarding for that user on that
 * device forever — before they had seen a single tender. Measured on prod over
 * one 6-day window: 125 signups, 139 saved searches, 21 `onboarding_completed`.
 *
 * The gate now allows up to 3 showings, at most one per calendar day, and only
 * writes `complete` when there is genuinely nothing left to ask. See
 * `lib/onboarding-gate.ts` for how those two numbers were sized against real
 * return behaviour.
 */
function markComplete(gate: OnboardingGateState): void {
  writeGate({ ...gate, complete: true });
}

/**
 * The sample's «💰 … · ⏰ …» line for a REAL first match: its own budget and
 * deadline, each omitted when the lot does not have it. `null` when it has
 * neither — the caller then prints no line rather than a made-up one.
 */
export function realSampleMeta(
  match: Pick<NicheFirstMatch, 'budgetUzs' | 'deadline'>,
  t: (key: string, params?: Record<string, string | number>) => string,
  locale: string,
): string | null {
  const parts: string[] = [];
  const amount = formatBudget(match.budgetUzs ?? null);
  if (amount) parts.push(t('onboarding.sample.budget', { amount }));
  const n = daysLeft(match.deadline ?? null);
  if (n !== null) {
    // `daysWord` declines Russian; Uzbek and Kazakh nouns do not agree with a
    // numeral, so they get the one form.
    const word = locale === 'uz' ? 'kun' : locale === 'kk' ? 'күн' : daysWord(n, 'ru');
    parts.push(t('onboarding.sample.daysLeft', { n, word }));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** A styled preview of a Telegram alert, on a chat-wallpaper gradient — "show,
 *  don't tell" for step 2.
 *
 *  ⚠️ Either ALL real or ALL canned (2026-10-06, Igor). It used to put the
 *  real first match's title over a fixed «💰 2 400 000 000 · ⏰ 12 дней» —
 *  a real lot with invented numbers, which a buyer reads as that lot's budget.
 *  With a real match the line is that lot's own budget and deadline; with none,
 *  the per-category canned example keeps its canned line.
 *
 *  A static PREVIEW, no actions (Igor, 06.10): it used to carry an «Открыть
 *  тендер →» chip that looked tappable and did nothing, a second «button» on a
 *  step whose one ask is «Подключить Telegram». */
function SampleAlert({
  category,
  realTender,
}: {
  category?: CategoryItem;
  realTender?: NicheFirstMatch | null;
}) {
  const { t, locale } = useLocale();
  const niche = category?.name ?? '';
  const slug = category?.slug ?? '';
  const perCat = slug ? t(`onboarding.sample.tender.${slug}`) : '';
  // Prefer a REAL tender from the user's just-picked niche (the instant
  // first-match) so the preview is genuinely personalized. Otherwise fall back
  // to a per-category canned example, then a generic one.
  const tender =
    realTender?.title ||
    (perCat && perCat !== `onboarding.sample.tender.${slug}`
      ? perCat
      : t('onboarding.sample.tender'));
  const meta = realTender?.title
    ? realSampleMeta(realTender, t, locale)
    : t('onboarding.sample.meta');
  return (
    <div className="overflow-hidden rounded-xl border border-brand/20 bg-gradient-to-br from-teal/25 via-brand-dim to-brand/20 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white shadow-m-sm">
          <BellIcon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[12px] font-semibold text-ink">{t('onboarding.sample.bot')}</span>
      </div>
      <div className="rounded-lg rounded-tl-sm border border-black/5 bg-bone-surface p-3 shadow-m-sm">
        <div className="text-[13px] font-bold text-ink">🔔 {t('onboarding.sample.title')}</div>
        <div className="mt-1 text-[12.5px] text-ink">{tender}</div>
        {niche && (
          <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11.5px] font-medium text-brand">
            <TagIcon className="h-3 w-3" />
            {niche}
          </div>
        )}
        {meta && <div className="mt-1.5 text-[12px] text-ink-soft">{meta}</div>}
      </div>
    </div>
  );
}

/**
 * First-login onboarding — a 2-step wizard, a bottom sheet on mobile and a
 * centered dialog on desktop:
 *  1. Pick a category — either a quick-pick chip or a search-box result. It
 *     auto-saves (a saved search unlocks alerts AND free supplier-contact reveals)
 *     and advances to step 2. Selecting = saving; there is no separate Next button.
 *  2. Connect Telegram (optional) — a persuasive step with a sample alert preview.
 * A notification only fires once both are done. Dismiss via ✕ or the overlay.
 */
/**
 * What to show the user when a save fails.
 *
 * A raw `e.message` used to reach the screen, which on a dropped mobile
 * connection is the browser's «Failed to fetch» — English, technical, and
 * about nothing the user did. A server refusal DOES carry a message worth
 * showing (over the free niche cap, for instance), so that one is kept.
 */
function describeSaveFailure(e: unknown, t: (k: string) => string): string {
  if (e instanceof NetworkError) return t('onboarding.niche.saveNetworkError');
  if (e instanceof Error && e.message) return e.message;
  return t('onboarding.niche.saveError');
}

export function OnboardingModal() {
  const { t, locale } = useLocale();
  const { user, status, refresh, completeTelegramBotLogin } = useAuth();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [gate, setGate] = useState(readGate);
  const [interacted, setInteracted] = useState(false);
  // The step being shown, as an ID rather than an index — the wizard now SKIPS
  // whatever is already done, so "step 2" is not a fixed thing. `null` until the
  // modal opens and picks the first pending step.
  const [step, setStep] = useState<OnboardingStepId | null>(null);
  /**
   * The step queue FROZEN at the moment the modal opened.
   *
   * ⚠️ Do not use the live `pendingSteps` for ordering or for the counter.
   * Completion is derived from account state, so the instant the user saves a
   * search that step LEAVES the live list — and the numbering shifts under
   * them: having just finished step 1 of 3 they were shown "ШАГ 1 ИЗ 2",
   * counting themselves backwards. The queue the user is walking has to stay
   * fixed for the duration of the visit; only `open` eligibility reads live
   * state.
   */
  const [plan, setPlan] = useState<OnboardingStepId[]>([]);
  // The «Название или ИНН» field's text — a name or digits; only digits are an
  // ИНН (`tinFromLookup`). A name reaches the API only as a PICKED suggestion,
  // whose ИНН «Продолжить» then submits.
  const [innText, setInnText] = useState('');
  const [innPick, setInnPick] = useState<CompanyPick | null>(null);
  const tin = tinFromLookup(innText);
  const [innBusy, setInnBusy] = useState(false);
  const [innError, setInnError] = useState<string | null>(null);
  const [innPayoff, setInnPayoff] = useState<CompanyPayoff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [categoryPicks, setCategoryPicks] = useState<CategoryItem[]>([]);
  // Precise ИКПУ niche picks (an alternative to the broad category chips).
  const [ikpuPicks, setIkpuPicks] = useState<IkpuItem[]>([]);
  // Curated Niche (Spec #3) picks — the third alternative on the same axis.
  const [nichePicks, setNichePicks] = useState<NicheItem[]>([]);
  // category slug → saved-search id, so removing a pick deletes its saved search.
  const [savedBySlug, setSavedBySlug] = useState<Record<string, string>>({});
  // ИКПУ code → saved-search id (same delete-on-remove bookkeeping).
  const [savedByIkpu, setSavedByIkpu] = useState<Record<string, string>>({});
  // Niche slug → saved-search id (same delete-on-remove bookkeeping).
  const [savedByNiche, setSavedByNiche] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // The instant first-match tender for the picked niche — powers the
  // personalized sample-alert preview on step 2 (falls back to a canned
  // example). The whole match, not just its title: the preview shows its real
  // budget and deadline too.
  const [firstMatch, setFirstMatch] = useState<NicheFirstMatch | null>(null);
  // The search saved in THIS visit, newest last — what the catalogue opens on
  // when the sheet closes (see the effect below `open`).
  const [lastSaved, setLastSaved] = useState<SavedSearch | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const landedRef = useRef(false);
  const [query, setQuery] = useState('');
  /**
   * The ИИ-подбор — `features.aiOnboarding` in the country config: on for
   * every Kazakh reader, off on Uzbekistan (owner, 2026-10-15).
   *
   * It swaps ONE step — the category picker — for «Что вы поставляете?». Off,
   * this file behaves exactly as it did, which is what keeps the existing
   * onboarding tests meaningful as a regression net for the Uzbek build.
   */
  const aiOn = useAiOnboarding();
  /**
   * The user asked for the picker instead — from the «выбрать категорию
   * вручную» link, or from one of the failure cards.
   *
   * One-way for this visit on purpose: someone who has just been told the model
   * did not understand them, or that their attempts are spent, must not be
   * dropped back on the same field by the next re-render.
   */
  const [aiManual, setAiManual] = useState(false);
  // Fires the email first-alert once on a telegram-off deployment — see `advance`.
  const emailFirstAlertRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // True only when a pointer press STARTS on the overlay itself — guards against
  // an accidental dismiss when a press begins inside the modal (e.g. selecting
  // text or a mis-drag) and releases on the backdrop.
  const overlayPress = useRef(false);
  const titleId = 'onboarding-title';
  const tinHintId = 'onboarding-tin-hint';
  // Part-typed ИНН: some digits, not yet all of them. Drives the length hint.
  const tinShort = tin.length > 0 && tin.length < TIN_DIGITS;

  const isPro =
    !!user?.subscription && !user.subscription.expired && user.subscription.plan !== 'free';
  const maxNiches = isPro ? 3 : 1;

  const authed = status === 'authed' && !!user;
  /*
   * ⚠️ NEVER over a checkout sheet.
   *
   * This modal is mounted once in `app-layout` and opens for a freshly
   * signed-in user with steps left. Since a guest's account is created by the
   * PAYMENT itself (`useGuestIdentify`), "freshly signed in" now happens
   * mid-checkout: on 2026-09-20 this opened on top of the card sheet the
   * instant `/auth/start` returned — before a single сум had moved — and hid
   * the form the buyer was filling in.
   *
   * Read below in BOTH places on purpose. In the opening effect it stops the
   * showing from being RECORDED, so a sheet that happens to be open does not
   * silently spend one of the three the gate allows; in `open` it keeps an
   * already-latched modal off the screen while a sheet is up. Either way
   * nothing is dismissed or retired — when the sheet closes this flips back and
   * onboarding opens on the next render exactly as it always did.
   */
  const checkoutSheetOpen = useCheckoutSheetOpen();
  const { data: searches, isLoading } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: authed && !dismissed,
  });

  const tgDone = Boolean(user?.telegramConnected);
  const hadSearch = (searches?.length ?? 0) > 0;

  const { data: categories } = useQuery({
    queryKey: ['categories', locale],
    queryFn: () => fetchCategories(locale),
    enabled: authed && !dismissed,
    staleTime: 5 * 60_000,
  });
  // All categories, sorted by live-tender count — the pool for both the
  // quick-pick chips and the search box. Comma-slug entries (the merged "ИТ и
  // цифровые технологии" facet = "it-telecom,telecom-equipment") are kept: they
  // are valid everywhere else (the catalog query and saved-search matching both
  // split on the comma), and excluding them made the whole IT niche unpickable
  // and unsearchable here.
  const allCategories = useMemo(
    () => (categories ?? []).slice().sort((a, b) => b.count - a.count),
    [categories],
  );
  const headCategories = useMemo(() => allCategories.slice(0, HEAD_COUNT), [allCategories]);

  // Unified search — one box over BOTH the broad categories (client-side) AND the
  // granular ИКПУ taxonomy (server-ranked by live-tender count), so "ноутбуки",
  // "цемент", "аудит" reach a precise niche, not just a 25-bucket category.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(id);
  }, [query]);

  const catResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    // Match the slug too, so a Latin "it" finds the IT niche even on the uz
    // locale (where its name is "IT va raqamli texnologiyalar", not Cyrillic).
    return allCategories
      .filter((c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q))
      .slice(0, 4);
  }, [query, allCategories]);

  const { data: ikpuHits, isFetching: ikpuFetching } = useQuery({
    queryKey: ['onboarding-ikpu-search', debouncedQuery, locale],
    queryFn: () => searchIkpu(debouncedQuery, locale),
    enabled: authed && !dismissed && debouncedQuery.length >= 2,
    staleTime: 60_000,
  });
  // Curated Niche (Spec #3) hits — searched in parallel, rendered ABOVE the
  // raw ИКПУ codes (mirrors the catalog filter-bar combobox).
  const { data: nicheHits, isFetching: nicheFetching } = useQuery({
    queryKey: ['onboarding-niche-search', debouncedQuery, locale],
    queryFn: () => searchNiches(debouncedQuery, locale),
    enabled: authed && !dismissed && debouncedQuery.length >= 2,
    staleTime: 60_000,
  });
  // Top ИКПУ niches (ranked by live-tender count) — shown the moment the search
  // box is focused, so ИКПУ browses like a select even before typing.
  const { data: ikpuFacets } = useQuery({
    queryKey: ['ikpu-facets', locale],
    queryFn: () => fetchIkpuCodes(locale),
    enabled: authed && !dismissed,
    staleTime: 5 * 60_000,
  });
  const topIkpu = useMemo(
    () => [...(ikpuFacets ?? [])].sort((a, b) => b.count - a.count).slice(0, 6),
    [ikpuFacets],
  );
  const searching = query.trim().length >= 2;
  const ikpuResults = searching ? (ikpuHits ?? []).slice(0, 6) : topIkpu;
  // Niches only surface while actively searching — there's no "top niches"
  // browse facet (unlike ИКПУ's `fetchIkpuCodes`).
  const nicheResults = searching ? (nicheHits ?? []).slice(0, 6) : [];
  // Categories shown in the panel: filtered when typing, top popular when
  // browsing (select-like) — mirrors the catalog niche combobox.
  const shownCats = searching ? catResults : headCategories.slice(0, 5);
  const moreCats = searching ? 0 : Math.max(0, allCategories.length - shownCats.length);
  const hasResults = catResults.length > 0 || nicheResults.length > 0 || ikpuResults.length > 0;
  // Show the dropdown on focus (browse), not only when typing.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!searchOpen) return;
    function onDoc(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node))
        setSearchOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchOpen]);
  // With the keyboard up, the dropdown ends above it rather than under it;
  // `undefined` everywhere else, so the `max-h-72` class keeps applying.
  const searchListMaxH = useKeyboardSpaceBelow(searchBoxRef, searchOpen, { ceiling: 288 });
  // Only declare "nothing found" once the debounce has settled AND both the
  // ИКПУ and Niche lookups are done — otherwise a pure-ИКПУ/niche query
  // ("ноутбук") flashes no-results.
  const searchSettled = debouncedQuery === query.trim() && !ikpuFetching && !nicheFetching;

  // A dismissal closes it for THIS visit only. The gate decides whether it ever
  // comes back — it does not, once the cap is spent or everything is done.
  const close = useCallback(() => setDismissed(true), []);

  // Swipe the mobile bottom sheet down to dismiss. `setPanelRef` is stable and
  // fans the node out to BOTH the focus/scroll ref and the gesture ref.
  // No `open` arg (unlike the subscribe/support sheets): onboarding never reopens
  // in-session — a dismissal retires it for the visit — so there is no leftover
  // transform to clear on reopen (and `open` is only computed below this line).
  const swipe = useSwipeDownToDismiss(close, true, undefined, ONBOARDING_GRAB_ZONE_PX);
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      swipe.ref.current = node;
    },
    [swipe.ref],
  );

  const steps = pendingSteps({
    hasSavedSearch: hadSearch,
    telegramConnected: tgDone,
    hasTin: Boolean(user?.tin),
    innSkipped: gate.innSkipped,
  });

  async function submitInn() {
    // A picked suggestion wins over the text: the field then shows its NAME.
    const value = innPick ? innPick.company.tin : tin;
    const via: CompanyEntry = innPick ? innPick.via : 'tin';
    if (value.length !== TIN_DIGITS) return;
    setInnError(null);
    setInnBusy(true);
    try {
      const result = await submitCompany(value, via);
      // Held in state and rendered in place. The payoff must NOT be lost to a
      // re-render the way the standalone card lost it — see company-prompt.tsx,
      // where `refresh()` setting `user.tin` unmounted the component before it
      // could show what it had just fetched.
      setInnPayoff(result);
      await refresh();
    } catch (err) {
      // A human phrase, never the API code (`invalid_tin` was shown verbatim
      // on KZ, 16.10.2026) — same mapping as the standalone company card.
      setInnError(t(companyErrorKey(err), { digits: TIN_DIGITS }));
    } finally {
      setInnBusy(false);
    }
  }

  /** Move to the next step that still needs doing; close when none are left. */
  const advance = useCallback(
    (from: OnboardingStepId) => {
      // An email-first-alert deployment (KZ, `messaging.emailFirstAlert`) fires
      // the onboarding first-alert that UZ fires on telegram-connect
      // (`handleTgAuth`) here instead — the moment the search is saved: the
      // delivery channel (a verified email) already exists from signup, and a KZ
      // account may never connect its bot. Guarded by a ref so picking several
      // categories (each advancing from 'search') triggers it once; the server
      // dedups too, also against a later Telegram connect.
      if (
        from === 'search' &&
        countryConfig.messaging.emailFirstAlert &&
        !emailFirstAlertRef.current
      ) {
        emailFirstAlertRef.current = true;
        void sendFirstAlert().catch(() => {});
      }
      const rest = plan.slice(plan.indexOf(from) + 1);
      if (rest.length === 0) setDismissed(true);
      else setStep(rest[0]);
    },
    [plan],
  );

  // ⚠️ `open` LATCHES — it is state, not a live re-read of the gate.
  //
  // Deriving it as `mayShowModal(gate, …)` is the obvious spelling and it makes
  // the modal close the instant it opens: `recordShown` stamps `lastShownDate`
  // with today, the once-per-day rule then rejects the very showing being
  // recorded, and the next render evaluates to false. The eligibility check
  // belongs at the moment of opening; after that only a dismissal closes it.
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (opened || dismissed || !authed || isLoading) return;
    if (checkoutSheetOpen) return;
    if (!mayShowModal(gate, steps.length > 0)) return;
    setOpened(true);
    setPlan(steps);
    setStep(steps[0]);
    const next = recordShown(gate);
    writeGate(next);
    setGate(next);
  }, [opened, dismissed, authed, isLoading, gate, steps.length, checkoutSheetOpen]);

  // Stays open while the user is engaged, even if finishing a step empties the
  // queue — otherwise connecting Telegram on the last step would yank the modal
  // away mid-tap. `interacted` mirrors the guard the previous auto-dismiss used.
  const open = opened && !dismissed && !checkoutSheetOpen && (interacted || steps.length > 0);
  // Keyboard-safe sheet height — see the hook for the full iOS story. Must sit
  // AFTER `open` is derived and BEFORE the `if (!open) return null` early exit
  // (hooks are unconditional); this modal stays mounted in app-layout, so a
  // constant `true` here would have pinned `--sheet-max` on every app page.
  useVisualViewportSheetHeight(open);

  /*
   * Closing after a save opens the catalogue ON that search (2026-10-06, Igor).
   *
   * It used to close onto the unfiltered catalogue — the reader had just said
   * what they sell, and the first screen after «Сохранено» ignored it. Now the
   * list is the saved search's own filter (`savedSearchToCatalogPath`, the
   * same link the sidebar uses) and `MySelectionLine` heads it with «Ваша
   * подборка: N открытых лотов».
   *
   * Only from the home page or the catalogue tree. Someone who signed up from
   * a lot page (to see its contacts) is put back where they were, not yanked to
   * a list. Once per visit, whichever way the sheet closed (✕, «Позже»,
   * «Готово», swipe, Escape, the last step).
   */
  useEffect(() => {
    if (!dismissed || landedRef.current || !lastSaved) return;
    landedRef.current = true;
    const bare = stripLocalePrefix(pathname);
    if (!(bare === '/' || bare === '' || bare === '/catalog' || bare.startsWith('/catalog/'))) {
      return;
    }
    const state: MySelectionState = { mySelection: true };
    void navigate(localePath(savedSearchToCatalogPath(lastSaved), locale), { state });
  }, [dismissed, lastSaved, pathname, navigate, locale]);

  // Nothing left to ask → retire onboarding for good. This is now the ONLY
  // path that writes the permanent flag.
  useEffect(() => {
    if (authed && !isLoading && steps.length === 0 && !gate.complete) {
      markComplete(gate);
      setGate((g) => ({ ...g, complete: true }));
    }
  }, [authed, isLoading, steps.length, gate]);

  // Page scroll behind the modal is locked by the `<RemoveScroll>` wrapper on
  // the return (iOS-correct, matches Radix Sheet) — see card-save-modal.

  // Modal a11y: move initial focus in, restore it on close, Escape-to-close, and
  // trap Tab within the dialog so keyboard focus can't escape to the page behind.
  useEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    const prevFocus = document.activeElement as HTMLElement | null;
    el?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !el) return;
      const f = el.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prevFocus?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  // Tapping a category auto-saves it and advances to the Telegram step.
  function onCategoryChange(next: CategoryItem[]) {
    const prev = categoryPicks;
    setCategoryPicks(next);
    setInteracted(true);

    for (const c of next.filter((c) => !prev.some((p) => p.slug === c.slug))) {
      setBusy(true);
      setSaveError(null);
      // `replace: true` — on the Free tier, below the cap (3 since 2026-10-04)
      // this is a plain create, so several picks accumulate; AT the cap the
      // server retires the OLDEST search instead of erroring, so picking a new
      // category in onboarding always "just works" (Pro ignores the flag).
      createNiche({ name: c.name, categorySlug: c.slug, replace: true })
        .then(async (r) => {
          setSavedBySlug((m) => ({ ...m, [c.slug]: r.search.id }));
          setFirstMatch(r.firstMatch ?? null);
          setLastSaved(r.search);
          await queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
          // Advance ONLY after the save succeeds — a failed save must not leave
          // the user on step 2 believing their category was saved.
          advance('search');
        })
        .catch((e: unknown) => {
          setSaveError(describeSaveFailure(e, t));
          // A pick is only rolled back when the SERVER refused it (over the free
          // cap, unauthorised). After a network failure the request settled
          // nothing, and clearing the choice punishes the user for their
          // connection — they tapped, we lost it, and the tap disappears.
          if (!(e instanceof NetworkError)) {
            setCategoryPicks((cur) => cur.filter((x) => x.slug !== c.slug));
          }
        })
        .finally(() => setBusy(false));
    }

    for (const c of prev.filter((c) => !next.some((n) => n.slug === c.slug))) {
      const id = savedBySlug[c.slug];
      setLastSaved((cur) => (cur && cur.id === id ? null : cur));
      setSavedBySlug((m) => {
        const copy = { ...m };
        delete copy[c.slug];
        return copy;
      });
      if (id) void deleteSavedSearch(id).catch(() => {});
      void queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
    }
  }

  // Pick a niche by ИКПУ code — the precise alternative to a broad category.
  // Same save-and-advance path (createNiche with ikpuCode → alerts match by the
  // ИКПУ hierarchy, so pushes stay on-target instead of catching a whole sector).
  function onIkpuChange(next: IkpuItem[]) {
    const prev = ikpuPicks;
    setIkpuPicks(next);
    setInteracted(true);

    for (const c of next.filter((c) => !prev.some((p) => p.code === c.code))) {
      setBusy(true);
      setSaveError(null);
      createNiche({ name: c.name, ikpuCode: c.code, replace: true })
        .then(async (r) => {
          setSavedByIkpu((m) => ({ ...m, [c.code]: r.search.id }));
          setFirstMatch(r.firstMatch ?? null);
          setLastSaved(r.search);
          await queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
          advance('search');
        })
        .catch((e: unknown) => {
          setSaveError(describeSaveFailure(e, t));
          // Rolled back only on a SERVER refusal — see the category handler.
          if (!(e instanceof NetworkError)) {
            setIkpuPicks((cur) => cur.filter((x) => x.code !== c.code));
          }
        })
        .finally(() => setBusy(false));
    }

    for (const c of prev.filter((c) => !next.some((n) => n.code === c.code))) {
      const id = savedByIkpu[c.code];
      setLastSaved((cur) => (cur && cur.id === id ? null : cur));
      setSavedByIkpu((m) => {
        const copy = { ...m };
        delete copy[c.code];
        return copy;
      });
      if (id) void deleteSavedSearch(id).catch(() => {});
      void queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
    }
  }

  // Pick a curated Niche (Spec #3) — the third alternative on the same axis
  // as category/ИКПУ. Same save-and-advance path (createNiche with nicheSlug
  // → alerts match the niche's underlying ИКПУ code set server-side).
  function onNicheChange(next: NicheItem[]) {
    const prev = nichePicks;
    setNichePicks(next);
    setInteracted(true);

    for (const c of next.filter((c) => !prev.some((p) => p.slug === c.slug))) {
      setBusy(true);
      setSaveError(null);
      createNiche({ name: c.name, nicheSlug: c.slug, replace: true })
        .then(async (r) => {
          setSavedByNiche((m) => ({ ...m, [c.slug]: r.search.id }));
          setFirstMatch(r.firstMatch ?? null);
          setLastSaved(r.search);
          await queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
          advance('search');
        })
        .catch((e: unknown) => {
          setSaveError(describeSaveFailure(e, t));
          // Rolled back only on a SERVER refusal — see the category handler.
          if (!(e instanceof NetworkError)) {
            setNichePicks((cur) => cur.filter((x) => x.slug !== c.slug));
          }
        })
        .finally(() => setBusy(false));
    }

    for (const c of prev.filter((c) => !next.some((n) => n.slug === c.slug))) {
      const id = savedByNiche[c.slug];
      setLastSaved((cur) => (cur && cur.id === id ? null : cur));
      setSavedByNiche((m) => {
        const copy = { ...m };
        delete copy[c.slug];
        return copy;
      });
      if (id) void deleteSavedSearch(id).catch(() => {});
      void queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
    }
  }

  // Pick a category from the search results — same save-and-advance path as a
  // chip tap (Free replaces the single pick; Pro appends until the cap).
  function pickCategory(cat: CategoryItem) {
    if (categoryPicks.some((p) => p.slug === cat.slug)) return;
    const next = !isPro
      ? [cat]
      : categoryPicks.length >= maxNiches
        ? categoryPicks
        : [...categoryPicks, cat];
    setQuery('');
    setSearchOpen(false);
    onCategoryChange(next);
  }

  // Pick a precise ИКПУ niche from the unified search — same save-and-advance
  // path (createNiche with ikpuCode → alerts match the exact product class).
  function pickIkpu(item: IkpuItem) {
    if (ikpuPicks.some((p) => p.code === item.code)) return;
    const next = !isPro ? [item] : ikpuPicks.length >= maxNiches ? ikpuPicks : [...ikpuPicks, item];
    setQuery('');
    setSearchOpen(false);
    onIkpuChange(next);
  }

  // Pick a curated Niche (Spec #3) from the unified search — same save-and-
  // advance path (createNiche with nicheSlug → alerts match its ИКПУ set).
  function pickNiche(item: NicheItem) {
    if (nichePicks.some((p) => p.slug === item.slug)) return;
    const next = !isPro
      ? [item]
      : nichePicks.length >= maxNiches
        ? nichePicks
        : [...nichePicks, item];
    setQuery('');
    setSearchOpen(false);
    onNicheChange(next);
  }

  // Step 2: the category is already saved, so this only links Telegram and fires
  // the first match. Re-reading the session flips tgDone → the step shows "done".
  function showTgError(e: unknown) {
    if (e instanceof AuthApiError && e.code === 'telegram_account_taken') {
      setError(t('telegram.connect.taken'));
    } else {
      setError(t('telegram.connect.error'));
    }
  }

  // Start in the bot (2026-10-18: the only way) already linked Telegram to this
  // session server-side: re-read it, then fire the first match.
  async function handleTgBotAuth(result: TelegramBotAuthResult) {
    setError(null);
    await completeTelegramBotLogin(result);
    await sendFirstAlert().catch(() => {});
  }

  return (
    <RemoveScroll>
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 md:items-center md:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onPointerDown={(e) => {
          overlayPress.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          // Close only when both press and release land on the overlay itself.
          if (e.target === e.currentTarget && overlayPress.current) close();
        }}
      >
        <div
          ref={setPanelRef}
          tabIndex={-1}
          /* `92vh` + the `supports-[height:1dvh]` twin, not a lone `dvh`
             (2026-09-26): `dvh` shipped in Chrome 108, and on Chrome 101 a `dvh`
             inside `max-height` computes to `none` — no cap at all — while
             `--sheet-lift` still applies. See the invoice sheet in
             `routes/plans.tsx` for the measurement. */
          className="relative mb-[var(--sheet-lift,0px)] max-h-[var(--sheet-max,92vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)] w-full overflow-y-auto overscroll-contain md:mb-0 rounded-t-2xl border border-edge bg-bone-surface p-5 shadow-xl outline-none md:max-w-md md:rounded-xl md:p-6"
          style={swipe.style}
          onClick={(e) => e.stopPropagation()}
          {...swipe.handlers}
        >
          {/* Bottom-sheet drag handle (mobile only) — see card-save-modal. */}
          <div className="-mt-1 mb-3 flex justify-center md:hidden">
            <span aria-hidden="true" className="h-1 w-10 rounded-full bg-charcoal/30" />
          </div>
          {/* h-9 and `text-ink`, not h-7 and `text-ink-muted`: at 28 px in the
              lightest ink on the palette this read as decoration on a phone,
              and it was the sheet's only exit. The `before:-inset-2` hit area
              is kept on top of the bigger box, so the tap target grows with
              it. */}
          <button
            type="button"
            onClick={swipe.dismiss}
            /* NOT «Позже» any more: the Telegram step now has a visible
               «Позже» text button of its own, and two controls sharing one
               accessible name is ambiguous for a screen reader (and for a
               test). The ✕ closes; the text button says what closing means. */
            aria-label={t('onboarding.modal.close')}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md text-[15px] text-ink transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone hover:text-ink"
          >
            ✕
          </button>

          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-brand">
            {t('onboarding.stepOf', {
              n: step ? plan.indexOf(step) + 1 : 1,
              total: plan.length || 1,
            })}
          </div>

          {step === 'search' && aiOn && !aiManual ? (
            /* ── ИИ-подбор ────────────────────────────────────────────────
               The whole state machine lives in the component; this file only
               says where the wizard goes next. `onSaved` does exactly what a
               manual pick does — refresh the list, keep the real first match
               for the sample alert, advance — so nothing downstream can tell
               the two arms apart. */
            <AiOnboardingStep
              titleId={titleId}
              onManual={() => {
                setInteracted(true);
                setAiManual(true);
              }}
              onSaved={(search, match) => {
                setInteracted(true);
                setFirstMatch(match ?? null);
                setLastSaved(search);
                void queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
                advance('search');
              }}
            />
          ) : step === 'search' ? (
            <>
              <h2
                id={titleId}
                className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
              >
                {t('onboarding.categories.title')}
              </h2>
              <p className="mb-4 text-[13px] leading-[1.55] text-ink-soft">
                {t('onboarding.categories.desc')}
              </p>

              {/* Unified niche search — a select + search: on focus it browses (top
                ИКПУ niches; categories are the chips below), and typing filters
                categories + searches all of ИКПУ. Picking saves + advances. */}
              <div ref={searchBoxRef} className="relative mb-2">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  placeholder={t('onboarding.categories.search')}
                  aria-label={t('onboarding.categories.search')}
                  autoComplete="off"
                  className="h-10 w-full rounded-lg border border-edge bg-bone-surface px-3 text-[16px] text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none"
                />
                {searchOpen && (
                  <div
                    style={searchListMaxH === undefined ? undefined : { maxHeight: searchListMaxH }}
                    className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-edge bg-bone-surface shadow-lg"
                  >
                    {/* Categories — top popular when browsing, filtered when typing. */}
                    {shownCats.length > 0 && (
                      <div className="bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                        {t('onboarding.categories.section')}
                      </div>
                    )}
                    {shownCats.map((cat) => (
                      <button
                        key={`cat-${cat.slug}`}
                        type="button"
                        onClick={() => pickCategory(cat)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-bone"
                      >
                        <span className="truncate">{cat.name}</span>
                        <span className="shrink-0 text-[11px] text-ink-muted">{cat.count}</span>
                      </button>
                    ))}
                    {moreCats > 0 && (
                      <div className="px-3 py-1.5 text-[12px] text-ink-muted">
                        {t('onboarding.categories.more', { count: moreCats })}
                      </div>
                    )}
                    {/* Curated Niches (Spec #3) — shown ABOVE the raw ИКПУ codes
                      (mirrors the catalog filter-bar combobox), tagged with a
                      small "ниша" badge. Reuses the ИКПУ row markup. */}
                    {nicheResults.length > 0 && (
                      <div className="border-t border-edge bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                        {t('niche.section')}
                      </div>
                    )}
                    {nicheResults.map((item) => (
                      <button
                        key={`niche-${item.slug}`}
                        type="button"
                        onClick={() => pickNiche(item)}
                        title={item.name}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-bone"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {/* The badge is decoration; the label is the option. Without
                            a separator the accessible name concatenated into
                            «НИШАСтроительство зданий» — one garbled word to a screen
                            reader, while sighted users see a clean pill. A trailing
                            space inside the badge is not enough (collapsed in the
                            a11y tree), so the separator is a real, hidden one. */}
                          <span
                            aria-hidden="true"
                            className="mr-1.5 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em] text-brand"
                          >
                            {t('niche.badge')}
                          </span>
                          <span className="sr-only">{t('niche.badge')}: </span>
                          {item.name}
                        </span>
                        {item.tenderCount > 0 && (
                          <span className="shrink-0 text-[11px] text-ink-muted">
                            {item.tenderCount}
                          </span>
                        )}
                      </button>
                    ))}
                    {/* Precise ИКПУ niches — top ones on focus, search hits when typing. */}
                    {ikpuResults.length > 0 && (
                      <div className="border-t border-edge bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                        {t('onboarding.ikpu.section')}
                      </div>
                    )}
                    {ikpuResults.map((item) => (
                      <button
                        key={`ikpu-${item.code}`}
                        type="button"
                        onClick={() => pickIkpu(item)}
                        title={`${item.code} ${item.name}`}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-bone"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="mr-1.5 font-mono text-[11px] text-ink-muted">
                            {item.code}
                          </span>
                          {item.name}
                        </span>
                        {item.count > 0 && (
                          <span className="shrink-0 text-[11px] text-ink-muted">{item.count}</span>
                        )}
                      </button>
                    ))}
                    {/* Browse hint on an empty box. */}
                    {!searching && (
                      <div className="px-3 py-2 text-[12px] italic text-ink-muted">
                        {t('onboarding.ikpu.hint')}
                      </div>
                    )}
                    {/* Typing states. Nothing found: a hint, not a link out — a
                        saved search needs a code, and the person already
                        thinks this box searches every tender (owner,
                        2026-10-16; `lib/ikpu-search-fallback.ts`). */}
                    {searching && !hasResults && searchSettled && (
                      <div className="px-3 py-2 text-[12.5px] text-ink-muted">
                        {t('filter.ikpu.noCodeHint')}
                      </div>
                    )}
                    {searching && !hasResults && !searchSettled && (
                      <div className="px-3 py-2 text-[12.5px] text-ink-muted">
                        {t('onboarding.categories.searching')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-edge bg-bone p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                  {t('onboarding.categories.popular')}
                </div>
                <CategoryChips
                  categories={headCategories}
                  value={categoryPicks}
                  isPro={isPro}
                  max={maxNiches}
                  onChange={onCategoryChange}
                />
                {saveError && (
                  <p className="mt-2 text-[12.5px] text-status-closing-text" role="alert">
                    {saveError}
                  </p>
                )}
              </div>
            </>
          ) : step === 'telegram' ? (
            <>
              <button
                type="button"
                onClick={() => setStep('search')}
                className="mb-2 text-[12.5px] font-semibold text-ink-muted transition-colors hover:text-ink"
              >
                {t('onboarding.back')}
              </button>
              <h2
                id={titleId}
                className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
              >
                {t('onboarding.tg.cta')}
              </h2>
              <p className="mb-4 flex items-center gap-1.5 text-[12.5px] font-semibold text-brand">
                <span aria-hidden>✓</span>
                {busy ? t('onboarding.niche.saving') : t('onboarding.saved')}
              </p>

              {/* Show whichever niche was saved — a category chip, an ИКПУ pick, or
                a curated Niche pick (neither has a category slug, so the sample
                uses its name + generic body). */}
              <SampleAlert
                category={
                  categoryPicks[0] ??
                  (ikpuPicks[0]
                    ? { slug: '', name: ikpuPicks[0].name, count: ikpuPicks[0].count }
                    : nichePicks[0]
                      ? { slug: '', name: nichePicks[0].name, count: nichePicks[0].tenderCount }
                      : undefined)
                }
                realTender={firstMatch}
              />

              {tgDone ? (
                <div className="mt-4">
                  <p className="mb-3 flex items-center gap-1.5 text-[13px] font-semibold text-brand">
                    <span aria-hidden>✓</span>
                    {t('onboarding.tg.connectedDone')}
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
                  >
                    {t('onboarding.done.close')}
                  </button>
                </div>
              ) : TG_BOT ? (
                // «Подключить Telegram» is the primary step-2 CTA (connecting is
                // the desired path) — through the bot since 2026-10-18, so there
                // is no telegram.org iframe to wait for; «Позже» below is the
                // QUIET way out, so the user is never stuck with only ✕.
                <div className="mt-4">
                  {/* Same component as the catalog strip, the catalog banner and
                    the dashboard card — one Telegram button on the whole site.
                    Block width: it is this step's primary CTA, so it matches the
                    «Закрыть» button below it. */}
                  <TelegramConnectButton
                    botUsername={TG_BOT}
                    onBotAuth={handleTgBotAuth}
                    onBotError={showTgError}
                    surface="onboarding"
                    block
                  />
                  {error && (
                    <p className="mt-2 text-[12.5px] text-status-closing-text" role="alert">
                      {error}
                    </p>
                  )}
                  {/* The always-available way out of the last step.
                    Until now this sheet's only exit on a phone was the ✕ in
                    the corner (and, once the 8s probe had elapsed, the
                    «Закрыть» that used to live in the block below). A step
                    whose one control is a third-party widget must not be a
                    room with no door.
                    Quiet text button, not a filled CTA: the widget is usually
                    just slow, and a prominent exit next to a button that is
                    about to appear talks people out of connecting. Same
                    `swipe.dismiss` as the ✕, so it animates out the same way. */}
                  <button
                    type="button"
                    onClick={swipe.dismiss}
                    className="mt-3 w-full text-center text-[12.5px] font-semibold text-ink-muted transition-colors hover:text-ink"
                  >
                    {t('onboarding.modal.later')}
                  </button>
                </div>
              ) : (
                // No Telegram bot configured — still offer a clear finish CTA.
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={close}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
                  >
                    {t('onboarding.done.close')}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* ── ИНН ────────────────────────────────────────────────────────────
             Last on purpose. By this point the user has picked a category and
             seen what the product does, which is the only moment asking for a
             government identifier is a fair trade. Asked first, it is how you
             collect `111111111`. */
            <>
              <h2
                id={titleId}
                className="mb-1 font-brand text-[20px] font-extrabold tracking-[-0.3px] text-ink"
              >
                {t('onboarding.inn.title')}
              </h2>
              <p className="mb-4 text-[13px] leading-[1.55] text-ink-soft">
                {t('onboarding.inn.subtitle')}
              </p>

              {innPayoff ? (
                /* One renderer, shared with the settings card. These four
                 branches used to be duplicated here, and adding the plural
                 interpolation to the strings updated the other copy only — so
                 this one rendered the literal `{{word}}` to real users. */
                <div className="mb-4 rounded-lg border border-brand/30 bg-brand/5 p-3">
                  <p className="text-[14px] font-semibold text-ink">
                    <CompanyPayoffLine payoff={innPayoff} />
                  </p>
                  {innPayoff.companyName && (
                    <p className="mt-1 text-[13px] text-ink-soft">{innPayoff.companyName}</p>
                  )}
                  {innPayoff.supplierLinkConflict && (
                    <p className="mt-1 text-[12.5px] text-status-closing-text" role="alert">
                      {t('claim.conflict.title')}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* One field for a name OR the ИНН (owner, 2026-10-18): on a
                    phone only 25 % answered this step with 9 digits, against
                    53 % on desktop — the name is to hand, the number is not.
                    A tapped suggestion is SELECTED, not submitted: the commit
                    stays «Продолжить», for a pick exactly as for typed digits
                    (see below for why nothing here auto-submits). */}
                  <CompanyLookup
                    className="mb-3"
                    value={innText}
                    onChange={(next) => {
                      setInnText(next);
                      setInnPick(null);
                      setInnError(null);
                    }}
                    picked={innPick}
                    onPick={(pick) => {
                      setInnText(pick.company.name);
                      setInnPick(pick);
                      setInnError(null);
                    }}
                    canSkip
                    describedBy={tinShort ? tinHintId : undefined}
                    disabled={innBusy}
                    surface="onboarding"
                  />
                  {/* Only while the number is part-typed: empty needs no hint
                    (the placeholder already says it), full needs none either.
                    Muted, not red — a short number mid-typing is not an error. */}
                  {tinShort && (
                    <p id={tinHintId} className="-mt-1.5 mb-3 text-[12.5px] text-ink-muted">
                      {t('onboarding.inn.digitsHint', { digits: TIN_DIGITS })}
                    </p>
                  )}
                </>
              )}

              {innError && (
                <p className="mb-2 text-[12.5px] text-status-closing-text" role="alert">
                  {innError}
                </p>
              )}

              {/* The primary button ONLY submits, and only a full-length ИНН
                or a company picked from the suggestions; moving on is its own
                quiet control underneath. Labelled «Продолжить» since
                2026-10-18 (owner: two buttons, «Продолжить» and «Пропустить»);
                «Проверить» below is its earlier name.

                It used to be ONE button whose label followed the field —
                «Пропустить» until the number was complete, «Проверить» once it
                was — so the step was never a dead end. The cost was that a
                half-typed number plus the only button on screen skipped the
                step silently: no ИНН saved, no error. Measured 18.09–04.10, of
                people who started typing an ИНН here, 63 of 254 on Android and
                31 of 207 elsewhere left without one, and none of them reached
                the server (every `POST /auth/me/company` in the log is a 200)
                — the button, not validation. So a short number now disables
                «Проверить» and says how many digits it needs, and skipping is
                something you choose rather than something an incomplete number
                does for you. Still never a dead end: «Пропустить» is always
                there until the payoff, empty field included.

                Deliberately NOT «У меня нет ИНН». That answer retires the step
                permanently, which is far stronger than "not now" — someone who
                simply hasn't got their ИНН to hand would be forced to declare
                they have none just to continue. It lives on the catalog banner
                instead, in one findable place rather than two half-visible ones.

                And deliberately not auto-submitting on the 9th keystroke, even
                though the category step commits on tap. A category is
                reversible; an ИНН is sticky — the step never returns once set,
                and the value drives supplier matching and the ИП checkout gate.
                One mistyped digit would silently link the wrong company. */}
              <button
                type="button"
                disabled={innBusy || (!innPayoff && !innPick && tin.length !== TIN_DIGITS)}
                onClick={() => {
                  if (innPayoff) return advance('inn');
                  void submitInn();
                }}
                className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60"
              >
                {innBusy
                  ? t('company.checking')
                  : innPayoff
                    ? t('onboarding.inn.next')
                    : t('company.submit')}
              </button>
              {!innPayoff && (
                // Same weight as «Позже» under the standalone form
                // (company-prompt.tsx): quiet and BELOW the commit, but a full-
                // width 36px row so it is still an easy tap on a phone.
                <button
                  type="button"
                  disabled={innBusy}
                  onClick={() => advance('inn')}
                  className="mt-1 inline-flex h-9 w-full items-center justify-center rounded-md text-[13px] font-semibold text-ink-muted transition-colors hover:text-ink disabled:pointer-events-none disabled:opacity-60"
                >
                  {t('onboarding.inn.skip')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </RemoveScroll>
  );
}
